import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Form as AntdForm } from 'antd';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { routes } from '../../../../../utils/helpers/ApiClient';
import AppBarCMS from '../../../../basic/Navigation/AppBarCMS/AppBarCMS';
import PhdPageContext from '../../../../common/Phd/PhdPageContext.jsx';
import { AdminContentBlock, Spinner } from '../../../../common';
import Form from '../../../../antd/form/Form/Form';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import { applyPhdValidationErrors } from '../utils/phdApiFeedback.js';
import {
  useGetPhdCommentQuery,
  useGetPhdThesesQuery,
  useSavePhdCommentMutation,
} from '../../../../../redux/services/phd';
import PhdCommentsForm from './PhdCommentsForm.jsx';
import { usePhdDirtyLeaveGuard } from '../usePhdDirtyLeaveGuard.jsx';

const PhdCommentsEdit = ({ mode }) => {
  const { id } = useParams();
  const isEdit = mode === 'edit';
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = AntdForm.useForm();
  const location = useLocation();
  const pageTitle = isEdit ? t('Upraviť komentár') : t('Pridať komentár');
  const createThesisId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const rawValue = params.get('thesisId');
    if (!rawValue) {
      return undefined;
    }
    const parsed = Number(rawValue);
    return Number.isFinite(parsed) ? parsed : undefined;
  }, [location.search]);

  useEffect(() => {
    document.title = `${pageTitle} - FRI CMS`;
  }, [pageTitle]);

  const { data: detail, isLoading: isLoadingDetail } = useGetPhdCommentQuery(
    id,
    {
      skip: !isEdit,
    },
  );
  const { data: theses, isLoading: isLoadingTheses } = useGetPhdThesesQuery();
  const [saveComment, { isLoading: isSaving }] = useSavePhdCommentMutation();
  const { saveSuccess, saveFailed } = useToastMessages();
  const returnUrl = routes.cmsPhdTheses;

  const thesisOptions = useMemo(() => {
    if (!theses) {
      return [];
    }
    const filtered = isEdit
      ? theses
      : theses.filter((thesis) => !thesis.approved_at && !thesis.archived_at);
    return filtered.map((thesis) => ({
      value: thesis.id,
      label: thesis.title_sk || thesis.title_en || `${t('Téma')} #${thesis.id}`,
    }));
  }, [isEdit, theses, t]);

  const initialValues = useMemo(() => {
    if (isEdit) {
      if (!detail) {
        return null;
      }
      return {
        phd_thesis_id: detail.phd_thesis_id,
        text: detail.text,
      };
    }
    return {
      phd_thesis_id: createThesisId,
      text: '',
    };
  }, [createThesisId, detail, isEdit]);
  const { modal, onSaveFailed, onSaveSuccess, onValuesChange } =
    usePhdDirtyLeaveGuard(form, initialValues);

  const handleSubmit = async (values) => {
    try {
      await saveComment({
        id: isEdit ? detail.id : undefined,
        body: values,
      }).unwrap();
      saveSuccess();
      onSaveSuccess(() => navigate(returnUrl));
    } catch (error) {
      onSaveFailed();
      applyPhdValidationErrors(form, error);
      saveFailed(error);
    }
  };

  if ((isEdit && (isLoadingDetail || !initialValues)) || isLoadingTheses) {
    return <Spinner />;
  }

  return (
    <>
      {modal}
      <AppBarCMS
        title={pageTitle}
        backUrl={returnUrl}
        backUrlTooltip={t('Témy dizertačných prác')}
        permissionsBasic="phd-comments"
      />
      <div style={{ margin: '10px' }}>
        <PhdPageContext
          title={t('Ako fungujú pripomienky k téme?')}
          description={t(
            'Školiteľ alebo člen pracovnej skupiny s príslušným oprávnením môže pridať pripomienku k dostupnej nepublikovanej téme. Jej vyriešením sa téma posunie do ďalšieho kroku posudzovania.',
          )}
        />
        <AdminContentBlock
          title={pageTitle}
          icon="fa fa-comments"
          link={returnUrl}
        >
          <Form
            form={form}
            initialValues={initialValues}
            onFinish={handleSubmit}
            onValuesChange={onValuesChange}
          >
            <PhdCommentsForm
              thesisOptions={thesisOptions}
              isEdit={isEdit}
              isSubmitting={isSaving}
            />
          </Form>
        </AdminContentBlock>
      </div>
    </>
  );
};

export default PhdCommentsEdit;
